
import React from 'react';
import { Editor } from '@tiptap/core';
import { Button, Card, Col, Dropdown, OverlayTrigger, Row, Tooltip } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignCenter, faAlignJustify, faAlignLeft, faAlignRight, faBold, faBracketsCurly, faCaretDown, faFontCase, faItalic, faListNumeric, faListUl, faParagraph, faTextSize, faUnderline } from '@fortawesome/pro-solid-svg-icons';
import { LIQUID_TEMPLATES_ACCOUNT } from '../mailers/templates/mailers_templates_liquid';

type TiptapEditorProps = {
  extraClasses?: string;
  editorRef: React.MutableRefObject<null>;
  tiptapInstance: React.MutableRefObject<Editor | null>;
  parentFontSize?: string;
  lite?: boolean;
  showDynamic?: boolean;
  maxHeight?: string;
}

type Level = 1 | 2 | 3 | 4 | 5 | 6;

const TEXT_STYLES = [
  { key: 'bold', label: 'Bold', icon: faBold },
  { key: 'italic', label: 'Italic', icon: faItalic },
  { key: 'underline', label: 'Underline', icon: faUnderline }
];

const FONT_FAMILIES = [
  { key: 'Courier Prime', label: 'Monospace' },
  { key: 'Tinos', label: 'Serif' }
]

const FONT_SIZES = ["8px", "10px", "12px", "16px", "20px", "24px", "28px", "32px", "36px", "40px", "48px", "54px", "64px", "72px", "96px"];

const TEXT_ALIGNMENT_OPTIONS = [
  { key: 'left', label: 'Left', icon: faAlignLeft },
  { key: 'center', label: 'Center', icon: faAlignCenter },
  { key: 'right', label: 'Right', icon: faAlignRight },
  { key: 'justify', label: 'Justify', icon: faAlignJustify }
];

const LIST_OPTIONS = [
  { key: 'bulletList', label: 'Bullet List', icon: faListUl },
  { key: 'orderedList', label: 'Ordered List', icon: faListNumeric }
];

export const TEXT_EDITOR_DOM_ID = 'rich-text-container';
export const DEFAULT_FONT_FAMILY = 'Arimo, sans-serif';

export default function TiptapEditor({
  extraClasses,
  editorRef,
  tiptapInstance,
  parentFontSize,
  lite = false,
  showDynamic = false,
  maxHeight = '73vh'
}: TiptapEditorProps) {
  const handleFontStyleChange = (style: string) => {
    switch (style) {
      case 'bold':
        tiptapInstance.current?.chain().focus().toggleBold().run();
        break;
      case 'italic':
        tiptapInstance.current?.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        tiptapInstance.current?.chain().focus().toggleUnderline().run();
        break;
      default:
        break;
    }
  }

  const handleListClick = (listType: string) => {
    switch (listType) {
      case 'bulletList':
        tiptapInstance.current?.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        tiptapInstance.current?.chain().focus().toggleOrderedList().run();
        break;
      default:
        break;
    }
  }

  const currentFontSize = tiptapInstance.current?.getAttributes('textStyle').fontSize || parentFontSize || '';

  const ICON_SHARED_PROPS = {
    className: 'squared p-1',
    variant: 'text'
  };

  return (
    <Card className={extraClasses}>
      <Card.Header className='shadow z-1'>
        <Row>
          {!lite && <Col className="border-end gap-2 d-flex" xs="auto">
            <OverlayTrigger
              key="paragraph-popover"
              overlay={
                <Tooltip id="tooltip-paragraph">
                  Font Style
                </Tooltip>
              }
              placement="top"
            >
              <Dropdown>
                <Dropdown.Toggle
                  className='p-0'
                  style={{ height: '1.5rem' }} // same height as squared to make it look consistent
                  variant='text'
                  id="dropdown-paragraph"
                >
                  <FontAwesomeIcon icon={faParagraph} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    href="#"
                    key="paragraph"
                    onClick={() => {
                      void tiptapInstance.current?.chain().focus().setParagraph().run();
                    }}
                  >
                    Paragraph
                  </Dropdown.Item>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Dropdown.Item
                      href="#"
                      key={`heading-${i + 1}`}
                      onClick={() => {
                        void tiptapInstance.current?.chain().focus().toggleHeading({ level: (i + 1) as Level }).run();
                      }}
                    >
                      H{i + 1}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </OverlayTrigger>
            <OverlayTrigger
              key="font-family-popover"
              overlay={
                <Tooltip id="tooltip-font-family">
                  Font Family
                </Tooltip>
              }
              placement="top"
            >
              <Dropdown>
                <Dropdown.Toggle
                  className='p-0'
                  style={{ height: '1.5rem' }} // same height as squared to make it look consistent
                  variant='text'
                  id="font-family-dropdown"
                >
                  <FontAwesomeIcon icon={faFontCase} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    href="#"
                    key="unset-font"
                    onClick={() => {
                      void tiptapInstance.current?.chain().focus().unsetFontFamily().run();
                    }}
                  >
                    Default Font
                  </Dropdown.Item>
                  {FONT_FAMILIES.map((font, i) => (
                    <Dropdown.Item
                      href="#"
                      key={`font-${i + 1}`}
                      onClick={() => {
                        void tiptapInstance.current?.chain().focus().setFontFamily(font.key).run();
                      }}
                    >
                      {font.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </OverlayTrigger>
            <OverlayTrigger
              key="text-size-popover"
              overlay={
                <Tooltip id="tooltip-text-size">
                  Font Size
                </Tooltip>
              }
              placement="top"
            >
              <Dropdown>
                <Dropdown.Toggle
                  className='p-0'
                  style={{ height: '1.5rem' }} // same height as squared to make it look consistent
                  variant='text'
                >
                  <FontAwesomeIcon icon={faTextSize} />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {FONT_SIZES.map((size, i) => (
                    <Dropdown.Item
                      className={`${currentFontSize === size ? 'active' : ''}`}
                      href="#"
                      key={`font-size-${i + 1}`}
                      onClick={() => {
                        tiptapInstance.current?.chain().focus().setFontSize(size).run();
                      }}
                    >
                      {size}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </OverlayTrigger>
          </Col>}
          <Col className="border-end" xs="auto">
            {TEXT_STYLES.map(option => (
              <OverlayTrigger
                key={`${option.key}-popover`}
                overlay={
                  <Tooltip id={`tooltip-text-style-${option.key}`}>
                    {option.label}
                  </Tooltip>
                }
                placement="top"
              >
                <Button
                  className={`${tiptapInstance.current?.isActive(option.key) ? 'active' : ''} ${ICON_SHARED_PROPS.className}`}
                  variant='text'
                  onClick={() => handleFontStyleChange(option.key)}
                >
                  <FontAwesomeIcon icon={option.icon} />
                </Button>
              </OverlayTrigger>
            ))}
          </Col>
          {!lite && <Col className="border-end" xs="auto">
            {TEXT_ALIGNMENT_OPTIONS.map(option => (
              <OverlayTrigger
                key={`${option.key}-popover`}
                overlay={
                  <Tooltip id={`tooltip-text-alignment-${option.key}`}>
                    {option.label}
                  </Tooltip>
                }
                placement="top"
              >
                <Button
                  {...ICON_SHARED_PROPS}
                  onClick={() => tiptapInstance.current?.chain().focus().setTextAlign(option.key).run()}
                >
                  <FontAwesomeIcon icon={option.icon} />
                </Button>
              </OverlayTrigger>
            ))}
          </Col>}
          {!lite && <Col className="border-end" xs="auto">
            {LIST_OPTIONS.map(option => (
              <OverlayTrigger
                key={`${option.key}-popover`}
                overlay={
                  <Tooltip id={`tooltip-text-size-${option.key}`}>
                    {option.label}
                  </Tooltip>
                }
                placement="top"
              >
                <Button
                  {...ICON_SHARED_PROPS}
                  onClick={() => handleListClick(option.key)}
                >
                  <FontAwesomeIcon icon={option.icon} />
                </Button>
              </OverlayTrigger>
            ))}
          </Col>}
          {showDynamic && <Col xs="auto">
            <OverlayTrigger
              overlay={
                <Tooltip id="tooltip-dynamic-fields">
                  Dynamic Fields
                </Tooltip>
              }
              placement="top"
            >
              <Dropdown>
                <Dropdown.Toggle
                  style={{ height: '1.5rem' }} // same height as squared to make it look consistent
                  variant='text'
                  className='p-0 px-1'
                  id="dropdown-merge-field"
                >
                  <FontAwesomeIcon icon={faBracketsCurly} />
                </Dropdown.Toggle>

                <Dropdown.Menu className="overflow-auto" style={{ maxHeight: '150px' }}>
                  {LIQUID_TEMPLATES_ACCOUNT.map(template => (
                    <Dropdown.Item
                      key={template.value}
                      href="#"
                      onClick={() => {
                        tiptapInstance.current?.chain().focus().insertContent(template.value).run();
                      }}
                      data-value={template.value}
                    >
                      {template.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </OverlayTrigger>
          </Col>}
        </Row>
      </Card.Header>
      {/* The ref attaches the Tiptap instance to this specific div */}
      <div
        id={TEXT_EDITOR_DOM_ID}
        className="overflow-y-auto d-flex flex-column h-100 p-4 bg-body"
        style={{ maxHeight: maxHeight }}
      >
        <div
          className="tiptap-editor-container bg-white rounded shadow-sm"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          ref={editorRef}
        />
      </div>
    </Card >
  );
}
